import {
  Button,
  makeStyles,
  SelectTabData,
  SelectTabEvent,
  Spinner,
  Tab,
  TabList,
  Text,
  tokens,
} from '@fluentui/react-components';
import {
  ArrowLeft24Regular,
  DocumentOnePageSparkleRegular,
  TextBulletListSquareSparkleRegular,
} from '@fluentui/react-icons';
import React, { useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  useGetRecordingQuery,
  useRenameMeetingMutation,
} from '../../queries/recording';
import { GlobalInformations } from '../../components/recording-global-informations';
import { MeetingNotes } from '../../components/meeting-notes';
import { MeetingTranscript } from '../../components/meeting-transcript';
import { useTeamsContext } from '../../providers/teams-context-provider';
import { canEditRecording } from '../../utils/can-edit-recording';

const useStyles = makeStyles({
  page: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    backgroundColor: tokens.colorNeutralBackground1,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalL}`,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  headerTitle: {
    flex: 1,
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase500,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  content: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
    padding: tokens.spacingHorizontalXL,
    overflowY: 'auto',
  },
  tabContent: {
    flex: 1,
    minHeight: 0,
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
    padding: tokens.spacingVerticalXXL,
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacingVerticalM,
    flex: 1,
    padding: tokens.spacingVerticalXXL,
  },
});

export const RecordingPage = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const { recordingId } = useParams<{ recordingId: string }>();
  const { data, isLoading, error } = useGetRecordingQuery(recordingId);
  const { mutate } = useRenameMeetingMutation();
  const { teamsContext } = useTeamsContext();

  const globalInfosRef = useRef<HTMLDivElement>(null);
  const [selectedTab, setSelectedTab] = useState<string>('meeting-notes');

  const editable = useMemo(() => {
    if (!data) return false;
    return canEditRecording(
      teamsContext?.user?.id,
      data.submittedBy,
      data.roster
    );
  }, [teamsContext?.user?.id, data]);

  const onTabSelect = (_: SelectTabEvent, data: SelectTabData) => {
    setSelectedTab(data.value as string);
  };

  const handleBack = () => {
    navigate('/');
  };

  if (isLoading) {
    return (
      <div className={styles.page}>
        <div className={styles.loadingContainer}>
          <Spinner size="large" label="Chargement de la réunion..." />
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className={styles.page}>
        <header className={styles.header}>
          <Button
            appearance="subtle"
            icon={<ArrowLeft24Regular />}
            onClick={handleBack}
          >
            Retour
          </Button>
        </header>
        <div className={styles.errorContainer}>
          <Text size={400}>Réunion introuvable</Text>
          <Text size={200}>
            Cette réunion n'existe pas ou vous n'y avez pas acces.
          </Text>
          <Button appearance="primary" onClick={handleBack}>
            Retour a l'accueil
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <Button
          appearance="subtle"
          icon={<ArrowLeft24Regular />}
          onClick={handleBack}
        >
          Retour
        </Button>
        <Text className={styles.headerTitle}>{data.name || 'Réunion sans titre'}</Text>
      </header>

      <div className={styles.content}>
        <GlobalInformations
          data={data}
          onRename={(name) => {
            if (recordingId) {
              mutate({ recordingId, name });
            }
          }}
          ref={globalInfosRef}
        />

        {data.report && (
          <>
            <TabList selectedValue={selectedTab} onTabSelect={onTabSelect}>
              <Tab
                id="meeting-notes"
                icon={<TextBulletListSquareSparkleRegular />}
                value="meeting-notes"
              >
                Compte-rendu
              </Tab>
              <Tab
                id="meeting-transcript"
                icon={<DocumentOnePageSparkleRegular />}
                value="meeting-transcript"
              >
                Transcription
              </Tab>
            </TabList>

            <div className={styles.tabContent}>
              {selectedTab === 'meeting-notes' && (
                <MeetingNotes
                  recordingId={recordingId!}
                  report={data.report}
                  globalInfosRef={globalInfosRef}
                  editable={editable}
                  userContext={data.userContext}
                  transcriptExpired={data.transcriptExpired}
                />
              )}
              {selectedTab === 'meeting-transcript' && (
                <MeetingTranscript
                  recordingId={recordingId!}
                  transcript={data.transcript}
                  editable={editable}
                  transcriptExpired={data.transcriptExpired}
                  transcriptExpiredAt={data.transcriptExpiredAt}
                />
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
